import type { CSSProperties, KeyboardEvent } from "react";
import { channelKey } from "@shared/protocol";
import {
  HashIcon,
  HeadphonesIcon,
  MicIcon,
  PowerIcon,
  ScreenIcon,
  SettingsIcon,
  SpeakerIcon,
} from "@/components/icons";
import { FriendsPanel } from "@/components/FriendsPanel";
import { InstallAppButton } from "@/components/InstallAppButton";
import { BRAND, C, FONT_MONO, formatShortStamp, hexA, initialOf, statusColor } from "@/theme";
import {
  can,
  colorOf,
  connectedVoiceChannel,
  dmEntries,
  onlineCount,
  participantsOf,
  pendingFriendRequests,
  roleOf,
  useApp,
  userById, unreadOf, mentionsOf } from "@/state/store";

/** Rotulo das secoes TEXTO / VOZ / CONVERSAS. */
const SECTION_LABEL: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".14em",
  color: C.textLabelDim,
};

/** Botao quadrado de 28px da faixa de voz e do rodape. */
const ICON_BUTTON: CSSProperties = {
  width: 28,
  height: 28,
  display: "grid",
  placeItems: "center",
  border: 0,
  borderRadius: 7,
  cursor: "pointer",
};

/** Icone das listas: o desenho aplica a opacidade no proprio SVG. */
const ROW_ICON: CSSProperties = { opacity: 0.75, flex: "0 0 auto" };

export function Sidebar() {
  const { state, actions } = useApp();
  const me = state.me;
  const meId = me?.id ?? "";
  const meRole = me ? roleOf(state, me.id) : undefined;
  const meColor = me ? colorOf(state, me.id) : state.settings.accent;
  const voiceRoom = connectedVoiceChannel(state);
  /** A tela é permissão própria: sem ela o botão aparece, mas explica por quê. */
  const podeTransmitir = can(state, "tela");

  const pedidos = pendingFriendRequests(state).length;
  /** Quem manda no que a lateral mostra é o trilho, não uma aba daqui. */
  const emMensagens = state.tab === "diretas";

  /**
   * Quem pode abrir as configurações DO SERVIDOR pelo cabeçalho.
   *
   * Basta uma das permissões administrativas: o modal já esconde sozinho
   * cada aba que a pessoa não pode ver, então exigir todas fecharia a porta
   * para quem administra só canais, por exemplo.
   */
  const podeConfigurar =
    !emMensagens &&
    (can(state, "canais") || can(state, "cargos") || can(state, "expulsar"));

  const abrirConfigDoServidor = (): void => {
    actions.setSettingsTab("geral");
    actions.openSettings();
  };
  // Mesmo flag de VoiceView/MobileApp: no navegador, transmitir com audio do
  // sistema ensurdece local pra quem esta transmitindo — o indicador de fala
  // tem que sumir igual ao "ensurdecer" normal, senao mente sobre quem da pra ouvir.
  const webLocalMuteForCapture =
    !Boolean(window.lfrcordDesktop) && state.sharing && state.shareSystemAudio;

  return (
    <div
      style={{
        width: 270,
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${C.line}`,
        background: C.panel,
      }}
    >
      {/*
        O cabeçalho vira BOTÃO quando há servidor aberto e a pessoa pode
        mexer nele. Antes, configurar canal, cargo ou membro exigia achar a
        engrenagem no rodapé, que é onde ficam as coisas da CONTA — o lugar
        errado para o que pertence ao servidor. Aqui é onde se procura: no
        nome dele.
      */}
      <div
        {...(podeConfigurar
          ? {
              role: "button",
              tabIndex: 0,
              onClick: abrirConfigDoServidor,
              onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  abrirConfigDoServidor();
                }
              },
              title: "Configurações do servidor",
            }
          : {})}
        className={podeConfigurar ? "hov-bg-1f" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: "16px 16px 14px",
          borderBottom: `1px solid ${C.lineSoft}`,
          cursor: podeConfigurar ? "pointer" : "default",
          textAlign: "left",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            borderRadius: 9,
            // Em Mensagens a marca já tem cor própria: fundo colorido atrás
            // dela sujaria o símbolo.
            background: emMensagens ? "transparent" : "var(--ac, #1F4FD8)",
            color: C.ink,
            fontWeight: 700,
            fontSize: 16,
          }}
        >
          {emMensagens ? (
            // Em Mensagens quem se apresenta é o app, não um servidor.
            <img src={BRAND.mark} alt="LFRCORD" style={{ width: 26, height: 26, display: "block" }} />
          ) : (
            state.settings.icon
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          {/*
            O cabeçalho segue o que o trilho selecionou: o nome do servidor
            aberto, ou "Mensagens" quando o botão de início está ativo. Mostrar
            sempre o servidor ficava mentiroso na lista de conversas diretas —
            elas não pertencem a servidor nenhum.
          */}
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: ".05em", lineHeight: 1.1 }}>
            {emMensagens ? "Mensagens" : state.settings.name}
          </div>
          <div
            style={{
              marginTop: 3,
              fontFamily: FONT_MONO,
              fontSize: 9,
              letterSpacing: ".12em",
              color: C.textLabel,
            }}
          >
            {emMensagens
              ? pedidos > 0
                ? `${pedidos} SOLICITAÇÃO(ÕES) ESPERANDO`
                : "AMIGOS E CONVERSAS"
              : `LFR CREW · ${onlineCount(state)} ONLINE`}
          </div>
        </div>
        {podeConfigurar ? (
          <span
            aria-hidden
            style={{
              marginLeft: "auto",
              flex: "0 0 auto",
              color: C.textMuted,
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : null}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px 12px" }}>
        {state.tab === "canais" ? (
          <>
            <div style={{ ...SECTION_LABEL, margin: "10px 8px 6px" }}>TEXTO</div>
            {state.channels.map(channel => {
              const key = channelKey(channel.id);
              const active = state.activeKey === key;
              const unread = unreadOf(state, key);
              const mentions = mentionsOf(state, key);
              return (
                <button
                  key={channel.id}
                  className="hov-bg-1f"
                  onClick={() => actions.selectChannel(channel.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "var(--ipy, 7px) 10px",
                    margin: "1px 0",
                    border: 0,
                    borderRadius: 8,
                    cursor: "pointer",
                    textAlign: "left",
                    background: active ? C.selected : "transparent",
                    color: active ? C.text : unread > 0 ? C.textStrong : C.idle,
                    transition: "background 130ms ease,color 130ms ease",
                  }}
                >
                  <HashIcon size={16} style={ROW_ICON} />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 14,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {channel.name}
                  </span>
                  {unread > 0 && !active ? (
                    <span
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "2px 7px",
                        borderRadius: 999,
                        background: "var(--ac, #1F4FD8)",
                        color: C.ink,
                      }}
                    >
                      {mentions > 0 ? `@${mentions}` : unread}
                    </span>
                  ) : null}
                </button>
              );
            })}

            <div style={{ ...SECTION_LABEL, margin: "16px 8px 6px" }}>VOZ</div>
            {state.voiceChannels.map(room => {
              const active = state.activeKey === `voice:${room.id}`;
              const people = participantsOf(state, room.id);
              const connected = state.voiceId === room.id;
              return (
                <div key={room.id} style={{ margin: "1px 0" }}>
                  <button
                    className="hov-bg-1f"
                    onClick={() =>
                      connected ? actions.selectVoiceView(room.id) : actions.joinVoice(room.id)
                    }
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      padding: "var(--ipy, 7px) 10px",
                      border: 0,
                      borderRadius: 8,
                      cursor: "pointer",
                      textAlign: "left",
                      background: active ? C.selected : "transparent",
                      color: active ? C.text : C.idle,
                      transition: "background 130ms ease",
                    }}
                  >
                    <SpeakerIcon size={16} style={ROW_ICON} />
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{room.name}</span>
                    {people.length > 0 ? (
                      <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.textFaint }}>
                        {people.length} na sala
                      </span>
                    ) : null}
                  </button>
                  {people.map(item => {
                    const user = userById(state, item.userId);
                    if (!user) return null;
                    const color = colorOf(state, item.userId);
                    // Deafened esconde os indicadores de fala, igual ao desenho.
                    const speaking =
                      !(state.deafened || webLocalMuteForCapture) &&
                      (state.speaking[item.peerId] ?? item.speaking);
                    return (
                      <div
                        key={item.peerId}
                        onContextMenu={event => {
                          event.preventDefault();
                          actions.openContextMenu(item.peerId, item.userId, event.clientX, event.clientY);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "3px 10px 3px 34px",
                          color: C.textMuted,
                        }}
                      >
                        <span
                          style={{
                            width: 20,
                            height: 20,
                            flex: "0 0 auto",
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "50%",
                            fontSize: 10,
                            fontWeight: 600,
                            background: hexA(color, 0.18),
                            color,
                          }}
                        >
                          {initialOf(user.name)}
                        </span>
                        <span style={{ fontSize: 12.5 }}>{user.name}</span>
                        {speaking ? (
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 999,
                              background: C.green,
                              animation: "blinkdot 1.2s ease-in-out infinite",
                            }}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        ) : (
          <>
            <FriendsPanel />
            <div style={{ ...SECTION_LABEL, margin: "10px 8px 6px" }}>CONVERSAS</div>
            {dmEntries(state).map(entry => {
              const active = state.activeKey === entry.key;
              const color = colorOf(state, entry.user.id);
              const preview = entry.last
                ? `${entry.last.authorId === meId ? "você: " : ""}${entry.last.body}`
                : "Nenhuma mensagem ainda";
              return (
                <button
                  key={entry.user.id}
                  className="hov-bg-1f"
                  onClick={() => actions.selectDm(entry.user.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    margin: "1px 0",
                    border: 0,
                    borderRadius: 8,
                    cursor: "pointer",
                    textAlign: "left",
                    background: active ? C.selected : "transparent",
                    color: C.textStrong,
                    transition: "background 130ms ease",
                  }}
                >
                  <span style={{ position: "relative", width: 32, height: 32, flex: "0 0 auto" }}>
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        fontSize: 13,
                        fontWeight: 600,
                        background: hexA(color, 0.18),
                        color,
                      }}
                    >
                      {initialOf(entry.user.name)}
                    </span>
                    <span
                      style={{
                        position: "absolute",
                        right: -1,
                        bottom: -1,
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        border: `2px solid ${C.panel}`,
                        background: statusColor(entry.user.status),
                      }}
                    />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 13.5, fontWeight: 600 }}>{entry.user.name}</span>
                      <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: C.textLabelDim }}>
                        {entry.last ? formatShortStamp(entry.last.createdAt) : ""}
                      </span>
                    </span>
                    <span
                      style={{
                        display: "block",
                        marginTop: 2,
                        fontSize: 12,
                        color: C.textDim,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {preview}
                    </span>
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>

      {voiceRoom ? (
        <div
          style={{
            padding: "10px 12px",
            borderTop: `1px solid ${C.lineSoft}`,
            background: C.panelVoice,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: C.green,
                animation: "blinkdot 1.4s ease-in-out infinite",
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 9.5,
                  letterSpacing: ".1em",
                  color: C.green,
                }}
              >
                VOZ CONECTADA
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "#d9d9d9", marginTop: 1 }}>
                {voiceRoom.name}
              </div>
            </div>
            {/*
              Era "Abrir sala": trocava a visão principal para a sala de voz.
              Duas coisas erradas nisso. O ícone é um monitor — o mesmo que o
              app usa para transmissão —, então todo mundo lia como
              "compartilhar tela". E quando a visão JÁ era a sala, o clique
              mandava ir para onde a pessoa já estava: nada acontecia, sem
              nenhum retorno. No lugar onde mais se clica, o botão era inerte.
              Agora ele faz o que o desenho promete. Abrir a sala continua a
              um clique, no nome dela na lista acima.
            */}
            <button
              className={podeTransmitir ? "hov-bg-24-fg" : undefined}
              onClick={actions.toggleShare}
              disabled={!podeTransmitir}
              title={
                podeTransmitir
                  ? state.sharing
                    ? "Parar de transmitir"
                    : "Transmitir a tela"
                  : "Você não tem permissão para compartilhar a tela."
              }
              style={{
                ...ICON_BUTTON,
                background: "transparent",
                color: state.sharing ? C.accent : C.textMuted,
                cursor: podeTransmitir ? "pointer" : "not-allowed",
                opacity: podeTransmitir ? 1 : 0.45,
              }}
            >
              <ScreenIcon size={15} />
            </button>
            <button
              className="hov-red"
              onClick={() => actions.leaveVoice()}
              title="Desconectar"
              style={{ ...ICON_BUTTON, background: "transparent", color: C.red }}
            >
              <PowerIcon size={15} />
            </button>
          </div>
        </div>
      ) : null}

      {/*
        Logo acima da identidade: é o último lugar que a pessoa olha antes de
        mexer nas próprias configurações, e o único que fica visível em
        qualquer aba. Ele mesmo decide se aparece — ver InstallAppButton.
      */}
      {me ? <InstallAppButton /> : null}

      {me ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: "10px 12px",
            borderTop: `1px solid ${C.lineSoft}`,
            background: C.panelFoot,
          }}
        >
          <span style={{ position: "relative", width: 32, height: 32, flex: "0 0 auto" }}>
            <span
              style={{
                width: 32,
                height: 32,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                fontSize: 13,
                fontWeight: 600,
                background: hexA(meColor, 0.18),
                color: meColor,
              }}
            >
              {initialOf(me.name)}
            </span>
            <span
              style={{
                position: "absolute",
                right: -1,
                bottom: -1,
                width: 10,
                height: 10,
                borderRadius: 999,
                border: `2px solid ${C.panelFoot}`,
                background: statusColor(me.status),
              }}
            />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.15 }}>{me.name}</div>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: 9.5,
                color: C.textLabel,
                marginTop: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {`@${me.handle}`}
              {meRole ? ` · ${meRole.name.toLowerCase()}` : ""}
            </div>
          </div>
          <button
            className="hov-bg-24"
            onClick={() => actions.toggleMute()}
            title="Microfone"
            style={{
              ...ICON_BUTTON,
              background: state.muted ? hexA(C.red, 0.16) : "transparent",
              color: state.muted ? C.red : C.textMuted,
            }}
          >
            <MicIcon size={15} off={state.muted} />
          </button>
          <button
            className="hov-bg-24"
            onClick={() => actions.toggleDeafen()}
            title="Áudio"
            style={{
              ...ICON_BUTTON,
              background: state.deafened ? hexA(C.red, 0.16) : "transparent",
              color: state.deafened ? C.red : C.textMuted,
            }}
          >
            <HeadphonesIcon size={15} off={state.deafened} />
          </button>
          <button
            className="hov-bg-24-fg"
            onClick={() => actions.openSettings()}
            title="Configurações"
            style={{ ...ICON_BUTTON, background: "transparent", color: C.textMuted }}
          >
            <SettingsIcon size={15} bg={C.panelFoot} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
